import { Form, useSearchParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { LoadingSpinner, LogoPurpleMark } from "../icons";
import { LoginPropsCommon } from "./typings";
import { classNames } from "~/utils";
import React from "react";
import { Button, Input } from "@mindstonehq/ui";

interface LoginSAMLProps extends LoginPropsCommon {
  error?: string;
  onSubmit: (email: string) => void;
  mode?: "login" | "signup";
}

export function SAML({ error: initialError, appRoutes, routerUtils, onSubmit, mode = "login" }: LoginSAMLProps) {
  const { Link } = routerUtils;
  const { t } = useLingui();
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(initialError);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  searchParams.delete("error");

  React.useEffect(() => {
    setTimeout(() => {
      emailRef.current?.focus();
    }, 700);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setLoading(true);

    onSubmit(email);
  };

  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-[rgba(0,0,0,0.1)] text-lg">
          <LoadingSpinner />
        </div>
      )}

      {/* Back Button - Absolutely positioned top-left */}
      <Link
        to={{
          pathname: mode === "signup" ? appRoutes.Signup : appRoutes.Login,
          search: searchParams.toString(),
        }}
        className="absolute top-6 left-6 flex items-center text-placeholder-foreground hover:text-foreground transition-colors z-10"
      >
        <ArrowLeftIcon className="h-5 w-5 mr-2" />
        <span className="text-sm"><Trans>Back</Trans></span>
      </Link>

      <div className="flex flex-col justify-center items-center w-full max-w-[400px] flex-1 gap-10">
        {/* Header */}
        <div className="text-center flex flex-col gap-2">
          {/* Mobile-only small brand mark */}
          <div className="lg:hidden flex justify-center mb-3">
            <LogoPurpleMark width={40} height={40} />
          </div>
          <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
            {mode === "signup" ? t`Sign up with SSO` : t`Sign in with SSO`}
          </h2>
          <p className="text-base leading-7 text-muted-foreground"><Trans>Enter your work email to continue</Trans></p>
        </div>

        {/* Form */}
        <div className="flex flex-col w-full">
          <Form method="post" className="w-full flex flex-col gap-6" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5 w-full">
              <label htmlFor="email" className="text-sm font-semibold leading-5 text-foreground">
                <Trans>Work email</Trans>
              </label>
              <Input
                type="email"
                name="email"
                id="email"
                placeholder={t`you@company.com`}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(undefined);
                }}
                ref={emailRef}
                required
                className={classNames(
                  "h-10 placeholder:text-placeholder-foreground",
                  error ? "border-destructive" : "",
                )}
              />
              {error && (
                <p className="text-sm text-destructive text-left mt-1" id="saml-error">
                  {error}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 w-full">
              <Button
                type="submit"
                className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
                disabled={!email}
              >
                <Trans>Continue</Trans>
              </Button>

              <div className="flex items-center justify-center gap-1 text-xs leading-5">
                <span className="text-theme-text-primary">
                  {mode === "signup" ? t`Already have an account?` : t`Don't have an account?`}
                </span>
                <Link
                  to={{
                    pathname: mode === "signup" ? appRoutes.Login : appRoutes.Signup,
                    search: searchParams.toString(),
                  }}
                  className="text-primary-hover hover:underline"
                >
                  {mode === "signup" ? t`Sign in` : t`Sign up`}
                </Link>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
}

export default SAML;
